import type { ConversationListItem, LiveConversationStateT } from '@chatbot/shared-types';
import { ChannelAvatar } from './ChannelAvatar.tsx';
import { StateBadge } from './StateBadge.tsx';

/** Franja de urgencia a la izquierda de cada fila (triage visual). */
const ACCENT: Record<LiveConversationStateT, string> = {
  pending_human: 'bg-warning-500',
  human_active: 'bg-success-500',
  bot_active: 'bg-info-500',
  resolved: 'bg-neutral-300 dark:bg-neutral-700',
};

interface Props {
  item: ConversationListItem;
  active: boolean;
  onSelect: (item: ConversationListItem) => void;
}

export function ConversationRow({ item, active, onSelect }: Props) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(item)}
        aria-current={active ? 'true' : undefined}
        className={`relative w-full text-left pl-5 pr-4 py-3 flex items-start gap-3 transition-colors group ${
          active
            ? 'bg-primary-50 dark:bg-primary-950/40'
            : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/60'
        }`}
      >
        <span aria-hidden className={`absolute left-0 top-0 bottom-0 w-1 ${ACCENT[item.state]}`} />

        <ChannelAvatar nombre={item.userDisplayName || item.userIdentifier} canal={item.channel} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span
              className={`font-semibold truncate ${
                active
                  ? 'text-primary-700 dark:text-primary-200'
                  : 'text-neutral-900 dark:text-neutral-100 group-hover:text-primary-600'
              }`}
            >
              {item.userDisplayName || item.userIdentifier}
            </span>
          </div>

          {item.lastMessagePreview && (
            <p className="text-sm text-neutral-600 dark:text-neutral-400 truncate">
              {item.lastMessagePreview}
            </p>
          )}

          <div className="mt-1.5 flex items-center gap-2">
            <StateBadge state={item.state} />
            {item.assignedAdminName && (
              <span className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate">
                por {item.assignedAdminName}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <time
            dateTime={item.lastMessageAt ?? item.stateChangedAt}
            className="text-[11px] text-neutral-500 dark:text-neutral-400 font-mono"
          >
            {relativeTime(item.lastMessageAt ?? item.stateChangedAt)}
          </time>
          {item.unreadCount > 0 && (
            <span
              className="min-w-[1.25rem] px-1.5 h-5 inline-flex items-center justify-center rounded-full bg-primary-600 text-white text-[11px] font-bold"
              aria-label={`${item.unreadCount} mensajes sin leer`}
            >
              {item.unreadCount > 99 ? '99+' : item.unreadCount}
            </span>
          )}
        </div>
      </button>
    </li>
  );
}

export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}
